<template>
	<div
		class="bp-dropdown"
		:class="{ className, 'bp-dropdown--sub': role }"
		ref="dropdown"
	>
		<button
			ref="trigger"
			aria-expanded="false"
			aria-haspopup="listbox"
			tabindex="0"
			:class="{
				[`bp-dropdown__${role ? 'sub' : 'btn'}`]: true,
				[`bp-dropdown__${role ? 'sub' : 'btn'}--active`]: !isHidden,
				[`${className}-bp__btn`]: className,
				[`${className}-bp__btn--active`]: !isHidden,
			}"
			@click="onToggle"
			@mouseenter="onBtnEnter"
			@mouseleave="onBtnLeave"
		>
			<slot name="btn"></slot>
			<slot name="icon" v-if="isIcon">
				<svg
					v-if="isLoading"
					class="bp-dropdown__icon bp-dropdown__icon--spin"
					viewBox="0 0 512 512"
				>
					<path
						fill="currentColor"
						d="M304 48c0 26.51-21.49 48-48 48s-48-21.49-48-48 21.49-48 48-48 48 21.49 48 48zm-48 368c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zm208-208c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.49-48-48-48zM96 256c0-26.51-21.49-48-48-48S0 229.49 0 256s21.49 48 48 48 48-21.49 48-48zm12.922 99.078c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.491-48-48-48zm294.156 0c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48c0-26.509-21.49-48-48-48zM108.922 60.922c-26.51 0-48 21.49-48 48s21.49 48 48 48 48-21.49 48-48-21.491-48-48-48z"
					></path>
				</svg>
				<svg
					v-else
					class="bp-dropdown__icon"
					:class="{ [`bp-dropdown__icon--${align}`]: align }"
					viewBox="0 0 256 512"
				>
					<path
						fill="currentColor"
						d="M119.5 326.9L3.5 209.1c-4.7-4.7-4.7-12.3 0-17l7.1-7.1c4.7-4.7 12.3-4.7 17 0L128 287.3l100.4-102.2c4.7-4.7 12.3-4.7 17 0l7.1 7.1c4.7 4.7 4.7 12.3 0 17L136.5 327c-4.7 4.6-12.3 4.6-17-.1z"
					></path>
				</svg>
			</slot>
		</button>
		<transition name="fade">
			<div
				v-if="!isHidden"
				class="bp-dropdown__body"
				ref="body"
				:id="id"
				:style="{
					minWidth: `${width}px`,
					top: `${top}px`,
					left: `${left}px`,
				}"
				:class="{ [`${className}-bp__body`]: className }"
				@click="onBodyClick"
				@mouseenter="onBodyEnter"
				@mouseleave="onBodyLeave"
			>
				<slot name="body"></slot>
			</div>
		</transition>
	</div>
</template>

<script setup>
import {
	ref,
	watch,
	onMounted,
	onBeforeUnmount,
	nextTick,
	getCurrentInstance,
} from "vue";

const props = defineProps({
	role: {
		type: String,
		required: false,
		default: "",
	},
	unscroll: {
		type: [HTMLElement, String],
		required: false,
		default: null,
	},
	align: {
		type: String,
		required: false,
		default: "bottom",
	},
	x: {
		type: Number,
		required: false,
		default: 0,
	},
	y: {
		type: Number,
		required: false,
		default: 0,
	},
	beforeOpen: {
		type: Function,
		required: false,
		default: (resolve) => resolve(),
	},
	trigger: {
		type: String,
		required: false,
		default: "click",
	},
	closeOnClick: {
		type: Boolean,
		required: false,
		default: false,
	},
	isIcon: {
		type: Boolean,
		required: false,
		default: true,
	},
	className: {
		type: String,
		required: false,
		default: "",
	},
	liSelector: {
		type: String,
		required: false,
		default: "input",
	},
});

const isHidden = ref(true);
const isLoading = ref(false);
const id = ref(null);
const timeout = ref(null);
const top = ref(undefined);
const left = ref(undefined);
const width = ref(undefined);
const listboxEl = ref(undefined);

const dropdown = ref("dropdown");

watch(isHidden, (newVal) => {
	if (props.unscroll) {
		const el =
			props.unscroll instanceof HTMLElement
				? props.unscroll
				: document.querySelector(props.unscroll);
		if (el) {
			el.style.overflow = !newVal ? "hidden" : "";
		}
	}
});

const generateRandomId = () => {
	return Math.random().toString(36).substring(2, 12);
};

const show = () => {
	isHidden.value = false;
};

const hide = () => {
	isHidden.value = true;
};

const setWidth = (newWidth) => {
	width.value = newWidth;
};

const setPosition = (btn, body) => {
	if (!btn || !body) {
		return;
	}

	const coords = getCoords(btn);

	const currentTop = coords.top;
	const currentLeft = coords.left;

	const btnWidth = btn.offsetWidth;
	const btnHeight = btn.offsetHeight;

	const bodyWidth = body.offsetWidth;
	const bodyHeight = body.offsetHeight;

	switch (props.align) {
		case "top":
			top.value = currentTop + scrollY - bodyHeight;
			left.value = currentLeft + scrollX;
			break;
		case "right":
			top.value = currentTop + scrollY;
			left.value = currentLeft + scrollX + btnWidth;
			break;
		case "bottom":
			top.value = currentTop + scrollY + btnHeight;
			left.value = currentLeft + scrollX;
			break;
		case "left":
			top.value = currentTop + scrollY;
			left.value = currentLeft + scrollX - bodyWidth;
			break;
		default:
			top.value = currentTop + scrollY + btnHeight;
			left.value = currentLeft + scrollX;
			break;
	}

	top.value += props.y;
	left.value += props.x;
};

const getCoords = (el) => {
	el = el.getBoundingClientRect();
	return {
		top: el.top - scrollY,
		left: el.left - scrollX,
	};
};

const onKeyUp = (evt) => {
	const key = evt.which || evt.keyCode;
	const activeElement = document.activeElement;
	const activeElementIsButton =
		activeElement.classList.contains("bp-dropdown__btn");
	const activeElementIsInput =
		activeElement.tagName == props.liSelector.toUpperCase();

	switch (key) {
		case utilities.keys.downarrow:
			evt.preventDefault();
			if (activeElementIsButton) {
				if (isHidden.value) {
					show();
				}
				nextTick(() => {
					const selector = dropdown.value.querySelector(
						`li ${props.liSelector}`
					);
					selector?.focus();
				});
			} else if (activeElementIsInput) {
				const nextLi = activeElement.closest("li").nextElementSibling;
				if (nextLi) {
					const selector = nextLi.querySelector(props.liSelector);
					selector?.focus();
				}
			}
			break;
		case utilities.keys.uparrow:
			evt.preventDefault();
			if (activeElementIsInput) {
				const prevLi =
					activeElement.closest("li").previousElementSibling;
				if (prevLi) {
					const selector = prevLi.querySelector(props.liSelector);
					selector?.focus();
				}
			}
			break;
		case utilities.keys.pageup:
			if (isHidden.value) {
				show();
			}
			nextTick(() => {
				let prevItem;
				const ad = listboxEl.value.getAttribute(
					"aria-activedescendant"
				);

				if (!ad) {
					return;
				}

				const currentItem = listboxEl.value.querySelector(`#${ad}`);
				prevItem = currentItem.previousElementSibling;

				if (prevItem) {
					listboxEl.value.insertBefore(currentItem, prevItem);
				}
			});
			break;
		case utilities.keys.pagedown:
			if (isHidden.value) {
				show();
			}
			nextTick(() => {
				let nextItem;
				const ad = listboxEl.value.getAttribute(
					"aria-activedescendant"
				);

				if (!ad) {
					return;
				}

				const currentItem = listboxEl.value.querySelector(`#${ad}`);
				nextItem = currentItem.nextElementSibling;

				if (nextItem) {
					listboxEl.value.insertBefore(nextItem, currentItem);
				}
			});
			break;
		case utilities.keys.end:
			if (isHidden.value) {
				show();
			}
			nextTick(() => {
				dropdown.value
					.querySelector(`li:last-of-type ${props.liSelector}`)
					.focus();
			});
			break;
		case utilities.keys.home:
			if (isHidden.value) {
				show();
			}
			nextTick(() => {
				dropdown.value.querySelector(`li ${props.liSelector}`).focus();
			});
			break;
		case utilities.keys.escape:
			utilities.emitter.emit("bp-dropdown:hide");
			break;
		default:
			break;
	}
};

const checkCustomCallback = (e) => {
	if (!isHidden.value) {
		prepare();
		return;
	}

	const promise = new Promise((resolve) => {
		isLoading.value = true;
		props.beforeOpen.call(this, resolve);
	});

	promise.then(() => {
		isLoading.value = false;
		if (!e.target.closest(".bp-dropdown__body")) {
			utilities.emitter.emit("bp-dropdown:open");
		}

		setTimeout(prepare, 0);
	});

	promise.catch(() => {
		throw Error("bp-dropdown promise error");
	});
};

const prepare = () => {
	isHidden.value = !isHidden.value;
	if (!isHidden.value) {
		nextTick(() => {
			const button = dropdown.value.firstElementChild;
			const container = document.getElementById(id.value);

			setWidth(button.offsetWidth);
			setPosition(button, container);
		});
	}
};

const onToggle = (e) => {
	if (isHidden.value) {
		utilities.setAriaProp(dropdown.value, "expanded", "true");
	} else {
		utilities.setAriaProp(dropdown.value, "expanded", "false");
	}
	if (props.trigger !== "click") {
		return;
	}

	checkCustomCallback(e);
};

const onBtnEnter = (e) => {
	if (props.trigger !== "hover" || !isHidden.value) {
		return;
	}

	checkCustomCallback(e);
};

const onBtnLeave = (e) => {
	if (props.trigger !== "hover") {
		return;
	}

	if (props.role) {
		timeout.value = setTimeout(() => (isHidden.value = true), 100);
	}

	const to = e.toElement;
	if (!to) {
		return;
	}

	const isDropdown =
		to.closest(".bp-dropdown__btn") || to.closest(".bp-dropdown__body");
	if (isDropdown) {
		return;
	}

	prepare();
};

const onBodyClick = () => {
	if (props.closeOnClick) {
		isHidden.value = true;
	}
};

const onBodyEnter = () => {
	if (timeout.value) {
		clearTimeout(timeout.value);
	}
};

const onBodyLeave = (e) => {
	if (props.trigger !== "hover") {
		return;
	}

	const to = e.toElement;
	if (!to) {
		return;
	}

	if (to.closest(".bp-dropdown__btn") || to.closest(".bp-dropdown__sub")) {
		return;
	}

	prepare();
};

onMounted(() => {
	const $root = getCurrentInstance().proxy.$root;

	utilities.emitter.on("bp-dropdown:open", () => (isHidden.value = true));
	utilities.emitter.on("bp-dropdown:hide", () => (isHidden.value = true));

	if (props.trigger === "click" && !$root["is-bp-dropdown"]) {
		Object.defineProperty($root, "is-bp-dropdown", {
			enumerable: false,
			configurable: false,
			writable: false,
			value: true,
		});

		document.onmousedown = (e) => {
			const target = e.target;
			const dropdown =
				target.closest(".bp-dropdown__btn") ||
				target.closest(".bp-dropdown__body");

			if (!dropdown) {
				utilities.emitter.emit("bp-dropdown:hide");
			}
		};
	}

	id.value = "bp-dropdown-" + generateRandomId();
	listboxEl.value = document.querySelector(".js-dropdown-listbox");
	dropdown.value.addEventListener("keyup", onKeyUp);
});

onBeforeUnmount(() => {
	dropdown.value.removeEventListener("keyup", onKeyUp);
	hide();
});
</script>

<style>
.bp-dropdown--sub {
	width: 100%;
}

.bp-dropdown--sub .bp-dropdown__btn,
.bp-dropdown--sub .bp-dropdown__sub {
	width: 100%;
}

.bp-dropdown--sub .bp-dropdown__icon {
	margin-left: auto;
}

.bp-dropdown__btn {
	display: inline-flex;
	align-items: center;
	padding: 3px 5px;
	border: 1px solid #efefef;
	cursor: pointer;
	transition: background-color 0.1s ease;
}

.bp-dropdown__sub {
	display: inline-flex;
	align-items: center;
}

.bp-dropdown__btn--active {
	background-color: #eee;
}

.bp-dropdown__icon {
	display: inline-block;
	width: 15px;
	height: 15px;
	overflow: visible;
	transition: transform 0.1s ease;
}

.bp-dropdown__icon--spin {
	width: 12px;
	height: 12px;
	animation: spin 2s infinite linear;
}

.bp-dropdown__icon--top {
	transform: rotate(-180deg);
}

.bp-dropdown__icon--right {
	transform: rotate(-90deg);
}

.bp-dropdown__icon--bottom {
	transform: rotate(0);
}

.bp-dropdown__icon--left {
	transform: rotate(-270deg);
}

.bp-dropdown__btn--active .bp-dropdown__icon--top,
.bp-dropdown__sub--active .bp-dropdown__icon--top {
	transform: rotate(0);
}

.bp-dropdown__btn--active .bp-dropdown__icon--right,
.bp-dropdown__sub--active .bp-dropdown__icon--right {
	transform: rotate(-270deg);
}

.bp-dropdown__btn--active .bp-dropdown__icon--bottom,
.bp-dropdown__sub--active .bp-dropdown__icon--bottom {
	transform: rotate(-180deg);
}

.bp-dropdown__btn--active .bp-dropdown__icon--left,
.bp-dropdown__sub--active .bp-dropdown__icon--left {
	transform: rotate(-90deg);
}

.bp-dropdown {
	position: relative;
}
.bp-dropdown__body {
	position: absolute !important;
	top: auto !important;
	left: auto !important;
	padding: 6px 8px;
	background-color: #fff;
	box-shadow: 0 5px 15px -5px rgba(0, 0, 0, 0.5);
	z-index: 9999;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.1s;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}

@keyframes spin {
	0% {
		transform: rotate(0);
	}
	100% {
		transform: rotate(360deg);
	}
}
</style>
